import React, { useState, useRef } from 'react';
import { Stack, Box, useTheme, Paper } from '@mui/material';
import { DeliveryStatus } from '@fdha/graphql-api-admin';
import { useGetDeliveryV2Query, TrackingStatus } from '@fdha/graphql-api-admin';
import {
  Loader,
  ShipmentStatusCard,
  Typography,
  Icon,
} from '@fdha/web-ui-library';
import { isDeliveryDelayed, SectionType } from '@fdha/common-utils';

import { MealDeliveryProvider } from '../../../hooks/useMealDeliveryContext';

import { ReviewEditDeliveryPage } from './ReviewDeliveryPage';
import { ChooseMealsPageWithContext } from './ChooseMeals/ChooseMealsPage';
import { getStyles } from './Delivery.styles';

interface DeliveryProps {
  patientId: string;
  subjectId?: string;
  foodProgramUserId?: string;
  deliveryMenuId?: string;
  deliveryId?: string;
  status?: DeliveryStatus | null;
  openChangesDate?: string;
  isCanceled?: boolean;
  isCanceledUpdateComment?: string | null;
  showWeekMenu?: boolean;
}

const DeliveryContent: React.FC<DeliveryProps> = ({
  patientId,
  subjectId = '',
  foodProgramUserId = '',
  deliveryMenuId = '',
  deliveryId = '',
  status,
  isCanceled = false,
  showWeekMenu = false,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [isReviewScreen, setIsReviewScreen] = useState(true);

  const scrollToTop = () => {
    const mainContainer = document.querySelector(
      '[role="tabpanel"][id^="tabpanel-"]'
    );
    mainContainer?.scrollTo({ top: 0 });
  };

  const { data: deliveryData, loading: loadingDelivery } =
    useGetDeliveryV2Query({
      variables: { deliveryId },
      fetchPolicy: 'cache-and-network',
      skip: !deliveryId,
    });

  const shipmentOrder = deliveryData?.deliveryV2?.order;
  const deliveryDate = deliveryData?.deliveryV2?.deliveryDate;

  const sectionType = useRef<SectionType>('entreeDishes');

  const handleAddItems = (itemType: SectionType) => {
    setIsReviewScreen(false);
    sectionType.current = itemType;
    scrollToTop();
  };

  const handleBackToReview = () => {
    setIsReviewScreen(true);
    scrollToTop();
  };

  const renderShipmentStatus = () => {
    // Hide shipment status when delivery is cancelled
    if (!shipmentOrder || isCanceled) return null;

    const shipmentLabels = shipmentOrder.shipmentLabels || [];

    if (shipmentLabels.length > 0) {
      return (
        <Stack spacing={3} sx={styles.shipmentContainer}>
          {shipmentLabels.map((label) => {
            const url = label.trackingUrl;

            return (
              <ShipmentStatusCard
                isOwnDelivery={false}
                status={label.trackingStatus}
                key={label.id}
                trackingId={label.trackingNumber ?? undefined}
                onTrackDelivery={
                  url ? () => window.open(url, '_blank') : undefined
                }
                isDelayed={
                  !!deliveryDate &&
                  isDeliveryDelayed(new Date(deliveryDate), [label])
                }
              />
            );
          })}
        </Stack>
      );
    }

    return (
      <Stack spacing={3} sx={styles.shipmentContainer}>
        <ShipmentStatusCard
          isOwnDelivery={false}
          status={TrackingStatus.BeingPrepared}
        />
      </Stack>
    );
  };

  const renderCancelMessage = () => {
    if (!isCanceled) return null;

    return (
      <Paper sx={styles.cancelMessageContainer} elevation={0}>
        <Typography variant="body1">The delivery has been canceled.</Typography>
        <Icon
          name="close-circle-outline"
          size="large"
          fill={theme.palette.text.primary}
          backgroundColor={theme.palette.grey[200]}
        />
      </Paper>
    );
  };

  if (loadingDelivery) {
    return <Loader />;
  }

  return (
    <Box>
      {/* Shipment Status Section */}
      {renderShipmentStatus()}
      {/* Cancel Message */}
      {renderCancelMessage()}
      {/* Main Delivery Edit Interface */}
      {isReviewScreen ? (
        <ReviewEditDeliveryPage
          patientId={patientId}
          deliveryMenuId={deliveryMenuId}
          deliveryId={deliveryId}
          foodProgramUserId={foodProgramUserId}
          status={status}
          isCanceled={isCanceled}
          onAddItems={handleAddItems}
          showWeekMenu={showWeekMenu}
        />
      ) : (
        <ChooseMealsPageWithContext
          patientId={patientId}
          subjectId={subjectId}
          deliveryMenuId={deliveryMenuId}
          deliveryId={deliveryId}
          foodProgramUserId={foodProgramUserId}
          itemType={sectionType.current}
          handleReviewSelection={handleBackToReview}
        />
      )}
    </Box>
  );
};

const Delivery: React.FC<DeliveryProps> = (props) => {
  return (
    <MealDeliveryProvider
      patientId={props.patientId}
      dietPlanDeliveryMenuId={props.deliveryMenuId}
      foodProgramUserId={props.foodProgramUserId}
      deliveryId={props.deliveryId}
      deliveryStatus={props.status || undefined}
      isCanceled={props.isCanceled}
    >
      <DeliveryContent {...props} />
    </MealDeliveryProvider>
  );
};

export default Delivery;
